import { Css, ProposedValue, useComputed, useTestIds } from "@homebound/beam";
import { Card } from "src/components/Card";
import { AiFieldProposalFormValue } from "src/components/formFields/ai/utils/aiFieldProposalForm";
import { findAiFieldProposal, parseAiNumber } from "src/components/formFields/ai/utils/bindAiProposal";
import { AiEntityProposalType, AiFieldProposalStatus, Maybe } from "src/generated/graphqlTypes";
import { PlanPackageElevationsV2_ReadyPlanOptionFragment } from "src/routes/libraries/plan-package-v2/components/ElevationsTab.generated";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { ObjectState } from "src/utils/formState";

type ElevationCardProps = {
  elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment;
  planCode: Maybe<string>;
  proposalType?: AiEntityProposalType;
  fieldProposals?: ObjectState<AiFieldProposalFormValue>[];
  onClick?: VoidFunction;
};

export function ElevationCard({ elevation, planCode, proposalType, fieldProposals = [], onClick }: ElevationCardProps) {
  const tid = useTestIds({}, "elevationCard");
  const { code, name, optionImage, programData: pd } = elevation;

  const hasPending = useComputed(
    () => fieldProposals.some((p) => p.status.value === AiFieldProposalStatus.Pending),
    [fieldProposals],
  );
  const isProposedCreate = hasPending && proposalType === AiEntityProposalType.Create;

  return (
    <Card xss={Css.p0.dg.$} onClick={onClick} {...tid}>
      <div
        css={
          Css.relative.w100
            .hPx(200)
            .bgGray200.add("backgroundImage", `url(${optionImage?.previewUrl ?? PLAN_FALLBACK_IMG})`)
            .add("backgroundSize", "cover")
            .add("backgroundPosition", "center").$
        }
      />
      <div css={Css.p3.df.fdc.aifs.gap2.asStretch.if(hasPending).bgPurple50.$}>
        <div>
          <div css={Css.sm.gray900.if(isProposedCreate).purple800.$} {...tid.code}>
            {planCode}
          </div>
          <div css={Css.xl.if(isProposedCreate).purple800.$} {...tid.title}>
            {code} - {name}
          </div>
        </div>
        <div
          css={Css.dg.gtc("1fr 1fr").asStretch.rgPx(4).cg4.sm.gray900.if(isProposedCreate).purple800.$}
          {...tid.stats}
        >
          <Stat
            proposal={findAiFieldProposal(fieldProposals, "sellableAboveGroundSqft")}
            original={`${sellableRange(elevation)} Sq Ft`}
            format={(v) => `${v} Sq Ft`}
          />
          {/* Extracted once per plan, not per elevation, so nothing is proposed for them — PLAN-1816 */}
          <span>Height: {pd.buildingHeightInFeet ?? "-"} ft</span>
          <span>Depth: {pd.depthInFeet ?? "-"} ft</span>
          <span>Width: {pd.widthInFeet ?? "-"} ft</span>
        </div>
      </div>
    </Card>
  );
}

type StatProps = {
  proposal: ObjectState<AiFieldProposalFormValue> | undefined;
  original: string;
  format: (value: number) => string;
};

/** One card stat, rendered as `original proposed` while a change to it is pending review. */
function Stat(props: StatProps) {
  const { proposal, original, format } = props;
  const proposed = useComputed(
    () =>
      proposal?.status.value === AiFieldProposalStatus.Pending ? parseAiNumber(proposal.fieldValue.value) : undefined,
    [proposal],
  );
  return proposed !== undefined ? (
    <ProposedValue original={original} proposed={format(proposed)} />
  ) : (
    <span>{original}</span>
  );
}

/** The elevation's own sellable sq ft, widened by options that add or subtract from it. */
function sellableRange(elevation: PlanPackageElevationsV2_ReadyPlanOptionFragment) {
  const base = elevation.programData.sellableSqft;
  if (base == null) return "-";
  // A record shared with one other option holds that option's impact on this elevation
  const deltas = elevation.programDatas
    .filter((pd) => pd.readyPlanOptions.length === 2)
    .map((pd) => pd.programData.sellableSqft ?? 0);
  const min = base + deltas.filter((d) => d < 0).sum();
  const max = base + deltas.filter((d) => d > 0).sum();
  return min === max ? `${base}` : `${min} - ${max}`;
}
